// Pure helpers for the "New Customers Secured" dashboard widget. Kept free of
// React/redux so the date-range building and the newly-secured-customer
// aggregation can be unit tested in isolation (mirrors customerPerformanceHelpers).
//
// Business logic (matches the "New Customer Sales" semantics): a Costar property
// is counted as a *newly secured customer* on the FIRST date its Costar Property
// Id (`costarId`) appears. We dedupe the GetCustomerInfo records by `costarId`,
// take the earliest `createdOn` as that customer's "secured date", then bucket
// that date into Week / Month-To-Date / Year-To-Date (and, for directors, per
// sales representative). The backend is queried over the year-to-date window, so
// "newly appearing" is determined within that window.

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const toYmd = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Normalize a date value (ISO datetime, e.g. "2026-06-15T09:00:00Z", or a plain
// "2026-06-15") to a YYYY-MM-DD string. Returns "" when absent/unparseable.
// ISO YYYY-MM-DD strings sort lexicographically, so callers can compare directly.
export const toDay = (value) => {
  if (!value) return "";
  const s = String(value);
  const isoDay = s.match(/^\d{4}-\d{2}-\d{2}/);
  if (isoDay) return isoDay[0];
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : toYmd(d);
};

// The three reporting windows, all "to-date" and anchored to `now`:
//   week  = start of the current calendar week (Sunday) → today
//   month = first of the current month → today  (Month-To-Date)
//   year  = Jan 1 of the current year → today    (Year-To-Date)
// `fromDate`/`toDate` are YYYY-MM-DD; `year.fromDate` is also the value sent to
// the API as FromDate so a single fetch covers all three windows.
export const getPeriodRanges = (now = new Date()) => {
  const year = now.getFullYear();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay()); // back up to Sunday
  const toDate = toYmd(now);
  return {
    week: { fromDate: toYmd(weekStart), toDate },
    month: { fromDate: `${year}-${pad(now.getMonth() + 1)}-01`, toDate },
    year: { fromDate: `${year}-01-01`, toDate },
  };
};

// Reduce raw GetCustomerInfo records to one entry per Costar property, keyed by
// `costarId`, holding the earliest secured day and the sales rep on that first
// record. Records without a usable `costarId` or `createdOn` are skipped.
export const firstSeenByCostar = (records) => {
  const byCostar = new Map();

  (Array.isArray(records) ? records : []).forEach((r) => {
    if (!r) return;
    const costarId = r.costarId != null ? String(r.costarId).trim() : "";
    const day = toDay(r.createdOn);
    if (!costarId || !day) return;
    const existing = byCostar.get(costarId);
    if (!existing || day < existing.securedDate) {
      byCostar.set(costarId, {
        securedDate: day,
        salesPerson: (r.salesPerson || "").trim() || "Unassigned",
        // salesPersonId is a numeric id on real records, so keep it as-is
        // (don't call String methods on it); treat null/undefined/"" as null.
        salesPersonId:
          r.salesPersonId != null && r.salesPersonId !== ""
            ? r.salesPersonId
            : null,
      });
    }
  });
  return byCostar;
};

const inRange = (day, range) => day >= range.fromDate && day <= range.toDate;

// Summarize newly secured customers from raw GetCustomerInfo records.
// Returns:
//   totals: { week, mtd, ytd }   distinct newly-secured Costar properties per window
//   reps:   [{ repName, mtd, ytd }]  per-sales-rep breakdown, sorted by ytd desc
// Each Costar property is counted once (at its first-seen date), so the same
// property never inflates the numbers.
export const summarizeNewCustomers = (records, now = new Date()) => {
  const ranges = getPeriodRanges(now);
  const firstSeen = firstSeenByCostar(records);

  const totals = { week: 0, mtd: 0, ytd: 0 };
  const repMap = new Map();

  firstSeen.forEach(({ securedDate, salesPerson, salesPersonId }) => {
    const isWeek = inRange(securedDate, ranges.week);
    const isMtd = inRange(securedDate, ranges.month);
    const isYtd = inRange(securedDate, ranges.year);
    if (isWeek) totals.week += 1;
    if (isMtd) totals.mtd += 1;
    if (isYtd) totals.ytd += 1;

    if (isMtd || isYtd) {
      const rep = repMap.get(salesPerson) || { repName: salesPerson, wtd:0, mtd: 0, ytd: 0, salesPersonId: salesPersonId };
      if (isMtd) rep.mtd += 1;
      if (isYtd) rep.ytd += 1;
      if (isWeek) rep.wtd += 1;
      repMap.set(salesPerson, rep);
    }
  });

  const reps = Array.from(repMap.values()).sort(
    (a, b) => b.ytd - a.ytd || a.repName.localeCompare(b.repName)
  );
  return { totals, reps };
};

// How many GetCustomerInfo pages are needed to cover `totalCount`, so the widget
// can fetch page 1, learn the total, then request the remaining pages in
// parallel. Always at least 1 (page 1 is fetched regardless) and capped at
// `maxPages` so a bad/huge TotalCount can never fan out unbounded.
export const pageCountForTotal = (totalCount, pageSize, maxPages) => {
  const total = Number.isFinite(totalCount) ? totalCount : 0;
  const pages = Math.ceil(total / pageSize) || 0;
  return Math.min(Math.max(pages, 1), maxPages);
};

// Cache key for a fetched summary: identifies the exact dataset by user, scope
// (salesPersonId) and the year-to-date window. Because `range.toDate` is "today",
// the key naturally changes each day, so a cached summary self-invalidates.
export const buildCacheKey = ({ userId, salesPersonId, range }) =>
  [userId ?? "", salesPersonId ?? "", range.fromDate, range.toDate].join("|");
